"use client";

import { formatDistanceToNow } from "date-fns";
import { cs, enUS } from "date-fns/locale";
import { CameraConnectivity } from "@/schemas/camera";
import { cn } from "@/lib/utils";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useLocalization } from "@/context/localization-context";
import type { TranslationKey } from "@/i18n/messages";
import { getCurrentLocale, translate } from "@/i18n/translate";
import { formatDateTime } from "@/lib/date-format";

type StatusKind = "online" | "offline" | "unknown";

const STATUS_STYLES: Record<
  StatusKind,
  { labelKey: TranslationKey; pill: string; dot: string }
> = {
  online: {
    labelKey: "cameras.status.online",
    pill: "border-green-200 bg-green-50 text-green-700 dark:border-green-900 dark:bg-green-950 dark:text-green-400",
    dot: "bg-green-500",
  },
  offline: {
    labelKey: "cameras.status.offline",
    pill: "border-red-200 bg-red-50 text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-400",
    dot: "bg-red-500",
  },
  unknown: {
    labelKey: "cameras.status.unknown",
    pill: "border-border bg-muted text-muted-foreground",
    dot: "bg-muted-foreground/50",
  },
};

const getStatusKind = (
  connectivity: CameraConnectivity | null | undefined,
): StatusKind => {
  if (!connectivity || connectivity.active === null) return "unknown";
  return connectivity.active ? "online" : "offline";
};

const formatTimestamp = (timestamp: number | null | undefined) =>
  timestamp ? formatDateTime(new Date(timestamp)) : translate("common.notAvailable");

const formatAgo = (timestamp: number | null | undefined) =>
  timestamp
    ? formatDistanceToNow(new Date(timestamp), {
        addSuffix: true,
        locale: getCurrentLocale() === "cs" ? cs : enUS,
      })
    : null;

type CameraStatusProps = {
  connectivity: CameraConnectivity | null | undefined;
  /** Show "last seen" next to the pill for offline cameras */
  showLastSeen?: boolean;
  className?: string;
};

export const CameraStatus = ({
  connectivity,
  showLastSeen = true,
  className,
}: CameraStatusProps) => {
  const { t } = useLocalization();
  const kind = getStatusKind(connectivity);
  const styles = STATUS_STYLES[kind];
  const lastSeen =
    kind === "offline"
      ? formatAgo(
          connectivity?.last_activity_time ??
            connectivity?.last_disconnect_time,
        )
      : null;

  return (
    <TooltipProvider delayDuration={200}>
      <Tooltip>
        <TooltipTrigger asChild>
          <div className={cn("inline-flex items-center gap-2", className)}>
            <span
              className={cn(
                "inline-flex items-center gap-1.5 rounded-full border px-2 py-0.5 text-xs font-medium",
                styles.pill,
              )}
            >
              <span className="relative flex h-2 w-2">
                {kind === "online" && (
                  <span
                    className={cn(
                      "absolute inline-flex h-full w-full animate-ping rounded-full opacity-75",
                      styles.dot,
                    )}
                  />
                )}
                <span
                  className={cn(
                    "relative inline-flex h-2 w-2 rounded-full",
                    styles.dot,
                  )}
                />
              </span>
              {t(styles.labelKey)}
            </span>
            {showLastSeen && lastSeen && (
              <span className="whitespace-nowrap text-xs text-muted-foreground">
                {lastSeen}
              </span>
            )}
          </div>
        </TooltipTrigger>
        <TooltipContent>
          {connectivity ? (
            <div className="grid grid-cols-[auto_auto] gap-x-3 gap-y-0.5">
              <span className="opacity-70">{t("cameras.status.lastConnect")}</span>
              <span>{formatTimestamp(connectivity.last_connect_time)}</span>
              <span className="opacity-70">{t("cameras.status.lastDisconnect")}</span>
              <span>{formatTimestamp(connectivity.last_disconnect_time)}</span>
              <span className="opacity-70">{t("cameras.status.lastActivity")}</span>
              <span>{formatTimestamp(connectivity.last_activity_time)}</span>
            </div>
          ) : (
            t("cameras.status.unavailable")
          )}
        </TooltipContent>
      </Tooltip>
    </TooltipProvider>
  );
};

export const CameraConnectivityDetails = ({
  connectivity,
}: {
  connectivity: CameraConnectivity | null | undefined;
}) => {
  const { t } = useLocalization();
  const items = [
    { label: t("cameras.status.lastConnect"), value: connectivity?.last_connect_time },
    { label: t("cameras.status.lastDisconnect"), value: connectivity?.last_disconnect_time },
    { label: t("cameras.status.lastActivity"), value: connectivity?.last_activity_time },
  ];

  return (
    <div className="grid grid-cols-3 gap-4">
      {items.map(({ label, value }) => (
        <div key={label}>
          <label className="text-sm font-medium text-muted-foreground">
            {label}
          </label>
          <div className="text-sm">{formatTimestamp(value)}</div>
          {formatAgo(value) && (
            <div className="text-xs text-muted-foreground">
              {formatAgo(value)}
            </div>
          )}
        </div>
      ))}
    </div>
  );
};
